import { useEffect, useState } from 'react'
import { Download, Plus, Trash2, Upload } from 'lucide-react'
import type { Profile, Rule } from '../../core/types.ts'
import { api } from '../lib/api.ts'
import { Shell } from '../components/Shell.tsx'
import { Button } from '../components/ui/button.tsx'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.tsx'
import { Input, Label, Select } from '../components/ui/input.tsx'
import { Alert } from '../components/ui/alert.tsx'
import { useToast } from '../components/ui/toast.tsx'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../components/ui/dropdown-menu.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import type { Theme } from '../lib/theme.ts'
import { useAdminMode } from '../lib/useAdminMode.tsx'
import { AdminNotice } from '../components/AdminNotice.tsx'

const RULE_HINTS: Record<Rule['type'], string> = {
  totalCount: 'Kolik děl má seznam obsahovat celkem',
  minInCategory: 'Kolik děl musí být z jednoho období',
  minByForm: 'Kolik děl musí být próza, poezie nebo drama',
  maxPerAuthor: 'Kolik děl smí být nejvýš od téhož autora',
  requiredWork: 'Konkrétní dílo, které škola vyžaduje',
}

const RULE_LABELS: Record<Rule['type'], string> = {
  totalCount: 'Celkový počet děl',
  minInCategory: 'Nejméně děl z kategorie',
  minByForm: 'Nejméně děl dané formy',
  maxPerAuthor: 'Nejvíc děl od jednoho autora',
  requiredWork: 'Povinné dílo',
}

export function SettingsPage({ theme, onToggleTheme }: { theme: Theme; onToggleTheme: () => void }) {
  const [profile, setProfile] = useState<Profile | null>(null)
  // Zůstává jako viditelný pruh, ne toast — je to připomínka, že profil je
  // sice nahraný, ale ještě neuložený, a to má vydržet, dokud uživatel neuloží.
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { locked } = useAdminMode()
  const toast = useToast()
  const [deleting, setDeleting] = useState<Rule | null>(null)

  useEffect(() => {
    api.profiles().then((ps) => setProfile(ps[0] ?? null)).catch((e) => setError(String(e.message ?? e)))
  }, [])

  if (!profile) {
    return <Shell theme={theme} onToggleTheme={onToggleTheme} title="Nastavení kritérií">{error ? <Alert variant="destructive">{error}</Alert> : <p>Načítám…</p>}</Shell>
  }

  const patch = (next: Partial<Profile>) => setProfile({ ...profile, ...next })

  /**
   * Každý typ pravidla nese jiná pole, takže nové pravidlo musí vzniknout
   * rovnou v platném tvaru — jinak by ho Zod na serveru odmítl.
   */
  function addRule(type: Rule['type']) {
    const id = `rule-${Date.now()}`
    const p = profile!
    const rule: Rule =
      type === 'totalCount' ? { id, type, min: 20, max: 20 }
      : type === 'minInCategory' ? { id, type, categoryId: p.categories[0]?.id ?? '', min: 1 }
      : type === 'minByForm' ? { id, type, formId: p.forms[0]?.id ?? '', min: 1 }
      : type === 'maxPerAuthor' ? { id, type, max: 2 }
      : { id, type, workId: '' }
    patch({ rules: [...p.rules, rule] })
  }

  async function save() {
    setError(null)
    try {
      await api.saveProfile(profile!)
      setStatus(null)
      toast('Uloženo.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Uložení se nepodařilo')
    }
  }

  function exportProfile() {
    const blob = new Blob([JSON.stringify(profile, null, 2)], { type: 'application/json' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = `kriteria-${profile!.id}.json`
    a.click()
    URL.revokeObjectURL(a.href)
  }

  async function importProfile(file: File) {
    setError(null)
    try {
      setProfile(JSON.parse(await file.text()) as Profile)
      setStatus('Profil načten — ještě ho ulož.')
    } catch {
      setError('Soubor se nepodařilo přečíst jako profil kritérií.')
    }
  }

  return (
    <Shell
      theme={theme}
      onToggleTheme={onToggleTheme}
      title="Nastavení kritérií"
      subtitle="Kategorie, formy a pravidla. Profil jde vyexportovat a poslat spolužákovi z jiné školy."
      actions={
        <>
          <Button variant="ghost" size="sm" onClick={exportProfile}><Download size={15} aria-hidden /> Export</Button>
          <Button variant="ghost" size="sm" asChild>
            <label className="cursor-pointer">
              <Upload size={15} aria-hidden /> Import
              <input type="file" accept="application/json" className="sr-only" onChange={(e) => e.target.files?.[0] && importProfile(e.target.files[0])} />
            </label>
          </Button>
          <Button size="sm" onClick={save} disabled={locked}>Uložit</Button>
        </>
      }
    >
      <AdminNotice what="Kritéria" />
      {error && <Alert variant="destructive" className="mb-4">{error}</Alert>}
      {status && <Alert className="mb-4">{status}</Alert>}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Kategorie</CardTitle>
            <p className="text-[length:var(--text-sm)] text-[var(--muted-foreground)]">
              Kategorie se dílům nepřiřazuje ručně — odvozuje se z roku vydání a původu. Meze určují, které dílo kam spadne.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {[...profile.categories].sort((a, b) => a.order - b.order).map((c, i) => (
              <div key={c.id} className="space-y-2 rounded-[var(--radius-lg)] border border-[var(--border)] p-3">
                <Input
                  value={c.label}
                  aria-label={`Název kategorie ${i + 1}`}
                  onChange={(e) => patch({ categories: profile.categories.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)) })}
                />
                {/* Každý štítek drží se svým polem ve vlastním boxu, aby ho
                    zalomení neodstřihlo od toho, co popisuje. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[length:var(--text-xs)]">
                  <span className="flex items-center gap-1.5">
                  <Label htmlFor={`from-${c.id}`}>od roku</Label>
                  <Input
                    id={`from-${c.id}`} type="number" inputSize="sm" className="w-24"
                    value={c.derive?.yearFrom ?? ''}
                    onChange={(e) => patch({ categories: profile.categories.map((x) => x.id === c.id ? { ...x, derive: { ...x.derive, yearFrom: e.target.value === '' ? undefined : Number(e.target.value) } } : x) })}
                  />
                  </span>
                  <span className="flex items-center gap-1.5">
                  <Label htmlFor={`to-${c.id}`}>do roku</Label>
                  <Input
                    id={`to-${c.id}`} type="number" inputSize="sm" className="w-24"
                    value={c.derive?.yearTo ?? ''}
                    onChange={(e) => patch({ categories: profile.categories.map((x) => x.id === c.id ? { ...x, derive: { ...x.derive, yearTo: e.target.value === '' ? undefined : Number(e.target.value) } } : x) })}
                  />
                  </span>
                  <span className="flex items-center gap-1.5">
                  <Label htmlFor={`origin-${c.id}`}>původ</Label>
                  <Select
                    id={`origin-${c.id}`} inputSize="sm" className="w-32"
                    value={c.derive?.origin ?? 'any'}
                    onChange={(e) => patch({ categories: profile.categories.map((x) => x.id === c.id ? { ...x, derive: { ...x.derive, origin: e.target.value as 'cs' | 'world' | 'any' } } : x) })}
                  >
                    <option value="any">jakýkoli</option>
                    <option value="cs">česká</option>
                    <option value="world">světová</option>
                  </Select>
                  </span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Formy</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {profile.forms.map((f, i) => (
              <Input
                key={f.id}
                value={f.label}
                aria-label={`Název formy ${i + 1}`}
                onChange={(e) => patch({ forms: profile.forms.map((x) => (x.id === f.id ? { ...x, label: e.target.value } : x)) })}
              />
            ))}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Pravidla</CardTitle>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={locked}>
                    <Plus size={14} aria-hidden /> Přidat pravidlo
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Typ pravidla</DropdownMenuLabel>
                  {(Object.keys(RULE_LABELS) as Rule['type'][]).map((type) => (
                    <DropdownMenuItem key={type} onSelect={() => addRule(type)}>
                      <span className="font-medium">{RULE_LABELS[type]}</span>
                      <span className="text-[length:var(--text-xs)] text-[var(--muted-foreground)]">{RULE_HINTS[type]}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {profile.rules.map((rule) => (
              <div key={rule.id} className="flex flex-wrap items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--border)] p-2.5 text-[length:var(--text-sm)]">
                {/* Na mobilu je název pravidla vlastní řádek — 208px napevno
                    v zalamovacím flexu se do 390px okna nevejde. */}
                <span className="w-full font-medium sm:w-52 sm:shrink-0">{RULE_LABELS[rule.type]}</span>

                {rule.type === 'minInCategory' && (
                  <Select
                    inputSize="sm" className="w-full sm:w-52" value={rule.categoryId} aria-label="Kategorie pravidla"
                    onChange={(e) => patch({ rules: profile.rules.map((r) => (r.id === rule.id ? { ...r, categoryId: e.target.value } as Rule : r)) })}
                  >
                    {profile.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </Select>
                )}
                {rule.type === 'minByForm' && (
                  <Select
                    inputSize="sm" className="w-full sm:w-52" value={rule.formId} aria-label="Forma pravidla"
                    onChange={(e) => patch({ rules: profile.rules.map((r) => (r.id === rule.id ? { ...r, formId: e.target.value } as Rule : r)) })}
                  >
                    {profile.forms.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                  </Select>
                )}

                {'min' in rule && (
                  <label className="flex items-center gap-1.5">
                    nejméně
                    <Input
                      type="number" min={0} inputSize="sm" className="w-20" value={rule.min}
                      aria-label="Minimální počet"
                      onChange={(e) => patch({ rules: profile.rules.map((r) => (r.id === rule.id ? { ...r, min: Number(e.target.value) } as Rule : r)) })}
                    />
                  </label>
                )}
                {'max' in rule && rule.type !== 'maxPerAuthor' && (
                  <label className="flex items-center gap-1.5">
                    nejvíc
                    <Input
                      type="number" min={0} inputSize="sm" className="w-20" value={rule.max ?? ''}
                      aria-label="Maximální počet"
                      onChange={(e) => patch({ rules: profile.rules.map((r) => (r.id === rule.id ? { ...r, max: e.target.value === '' ? undefined : Number(e.target.value) } as Rule : r)) })}
                    />
                  </label>
                )}
                {rule.type === 'maxPerAuthor' && (
                  <label className="flex items-center gap-1.5">
                    nejvíc
                    <Input
                      type="number" min={1} inputSize="sm" className="w-20" value={rule.max}
                      aria-label="Maximum od jednoho autora"
                      onChange={(e) => patch({ rules: profile.rules.map((r) => (r.id === rule.id ? { ...r, max: Number(e.target.value) } as Rule : r)) })}
                    />
                  </label>
                )}

                <Button
                  size="sm" variant="ghost" className="ml-auto text-[var(--destructive)]"
                  aria-label={`Smazat pravidlo ${RULE_LABELS[rule.type]}`}
                  disabled={locked}
                  onClick={() => setDeleting(rule)}
                >
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Smazat pravidlo?"
        description={
          <>
            Pravidlo „{deleting ? RULE_LABELS[deleting.type] : ''}" zmizí z profilu kritérií
            a přestane se kontrolovat ve <strong>všech seznamech</strong>, které z profilu
            vycházejí. Projeví se to až po uložení.
          </>
        }
        confirmLabel="Smazat pravidlo"
        destructive
        onConfirm={() => {
          patch({ rules: profile.rules.filter((r) => r.id !== deleting?.id) })
          setDeleting(null)
        }}
      />
    </Shell>
  )
}